import { cellView, meanOf, runStats, type RunStats } from "@/components/batch-test/batchCells";
import { specKey, teLetters } from "@/components/batch-test/teSpec";
import type { BatchCell, BatchRunSummary, MatrixPage, TeSpec } from "@/types";
import { evaluationOutcomeFrom } from "@/utils/evaluation";
import { DELTA_EPSILON, isCommon, isComparable } from "@/utils/rubric";
import { validLatencyMs } from "./latency";

// Scale rules live with the rubric; re-exported for the screens that read
// them from here.
export { DELTA_EPSILON, formatDelta } from "@/utils/rubric";

/**
 * One Matrix column — a TE that actually ran on the page
 * (fe/TEST_CASES_RESULT.md §3.1). Several runs can share a TE (re-runs of
 * the same diff); the column shows one of them.
 */
export type MatrixColumn = {
  key: string;
  letter: string;
  spec: TeSpec;
  /**
   * The run whose cells the column shows: the newest finished one, so a
   * re-run in progress does not blank a column that already has results.
   * A TE whose only run is still going shows that run, live states and all.
   */
  run: BatchRunSummary;
  runCount: number;
  /** TE-A — the page's first TE, what every other column is compared with. */
  isBaseline: boolean;
};

export function matrixColumns(page: MatrixPage): MatrixColumn[] {
  const letters = teLetters(page.runs);
  const byKey = new Map<string, BatchRunSummary[]>();
  // `runs` is newest first, so each list is too.
  for (const run of page.runs) {
    const key = specKey(run.te_spec ?? {});
    byKey.set(key, [...(byKey.get(key) ?? []), run]);
  }
  return [...byKey.entries()]
    .map(([key, runs]) => {
      const letter = letters.get(key) ?? "TE-?";
      return {
        key,
        letter,
        spec: runs[0].te_spec ?? {},
        run: runs.find((r) => r.status !== "running") ?? runs[0],
        runCount: runs.length,
        isBaseline: letter === "TE-A",
      };
    })
    .sort((a, b) => a.letter.localeCompare(b.letter));
}

export function matrixCell(
  page: MatrixPage,
  column: MatrixColumn,
  testCaseId: string,
): BatchCell | undefined {
  return page.cells[column.run.batch_run_id]?.[testCaseId];
}

/** Only the run displayed in this TE column, over the page's fixed TC set. */
export function matrixColumnStats(page: MatrixPage, column: MatrixColumn) {
  const cells = page.cells[column.run.batch_run_id];
  const stats = runStats(cells, page.test_case_ids);
  const latencies = page.test_case_ids.flatMap((id) => {
    const cell = cells?.[id];
    const value = cell?.status === "done" ? validLatencyMs(cell.latency_ms) : null;
    return value == null ? [] : [value];
  });
  return { ...stats, avgLatencyMs: meanOf(latencies), latencyMeasured: latencies.length };
}

/**
 * The number a cell contributes, or null — only a scored verdict has one (C5),
 * and only on the current rubric: a v1 score is shown but never averaged or
 * compared with v2's (be/RUBRIC.md R10).
 */
export function cellScore(cell: BatchCell | undefined): number | null {
  if (!cell || cell.status !== "done") return null;
  const outcome = evaluationOutcomeFrom(cell.evaluation);
  return outcome.status === "scored" && isComparable(outcome) ? outcome.score : null;
}

// --- 결과 정리 (fe/TEST_CASES_RESULT.md §6) ----------------------------------


export type ColumnSummary = {
  column: MatrixColumn;
  stats: RunStats;
  /** Against the baseline column's mean; null on the baseline or with no mean. */
  delta: number | null;
  /** Per TC, both sides scored. Null on the baseline itself. */
  improved: number | null;
  worsened: number | null;
  same: number | null;
};

export type Mover = {
  testCaseId: string;
  column: MatrixColumn;
  from: number;
  to: number;
  delta: number;
};

export type Unscored = {
  testCaseId: string;
  column: MatrixColumn;
  cell: BatchCell | undefined;
  label: string;
  reason: string | null;
};

export type PageSummary = {
  columns: ColumnSummary[];
  /** Mean of every comparable scored cell the Matrix shows. */
  avg: number | null;
  /** v2's other two scores over the same cells (R3.1). */
  avgAnswerQuality: number | null;
  avgExecution: number | null;
  combos: number;
  scored: number;
  failed: number;
  /** No approved Reference — scoring will not help. */
  notEvaluable: number;
  /** An answer waiting for the scoring pass. */
  notScoredYet: number;
  /** Scored on another rubric or policy — shown, left out of every mean (R10). */
  otherRubric: number;
  /** Cells scored with the common rubric — counted apart, averaged only among themselves (R16). */
  commonRubric: number;
  improved: Mover[];
  worsened: Mover[];
  unscored: Unscored[];
};

/**
 * The page's results, in the questions fe/TEST_CASES_BATCH.md §5 asked:
 * each TE's mean, which TCs moved against the baseline, and which cells
 * have no score and why.
 *
 * **Improved/worsened never mixes in an unscored cell** — a TC counts only
 * when both the baseline and the TE scored it. Otherwise a TC that lost its
 * Reference would read as a regression.
 */
function isCommonCell(cell: BatchCell | null | undefined): boolean {
  const outcome = evaluationOutcomeFrom(cell?.evaluation);
  return outcome.status === "scored" && isCommon(outcome);
}

export function summarizePage(page: MatrixPage): PageSummary {
  const columns = matrixColumns(page);
  const ids = page.test_case_ids;
  const base = columns.find((c) => c.isBaseline) ?? null;
  const baseScores = new Map(
    base ? ids.map((id) => [id, cellScore(matrixCell(page, base, id))] as const) : [],
  );
  const baseAvg = base ? runStats(page.cells[base.run.batch_run_id], ids).avg : null;

  const movers: Mover[] = [];
  const unscored: Unscored[] = [];
  let scored = 0;
  let failed = 0;
  let notEvaluable = 0;
  let notScoredYet = 0;
  let otherRubric = 0;
  let commonRubric = 0;
  let sum = 0;
  const answerQuality: (number | null | undefined)[] = [];
  const execution: (number | null | undefined)[] = [];

  const summaries = columns.map((column) => {
    const stats = runStats(page.cells[column.run.batch_run_id], ids);
    let improved = 0;
    let worsened = 0;
    let same = 0;
    for (const id of ids) {
      const cell = matrixCell(page, column, id);
      const score = cellScore(cell);
      if (score != null) {
        scored += 1;
        sum += score;
        const outcome = evaluationOutcomeFrom(cell?.evaluation);
        if (outcome.status === "scored") {
          answerQuality.push(outcome.answer_quality);
          execution.push(outcome.execution);
        }
      } else if (isCommonCell(cell)) {
        // Its own measure (R16): counted apart, never a "no score" row.
        commonRubric += 1;
      } else {
        const view = cellView(cell);
        if (view.tone === "failed") failed += 1;
        if (view.tone === "na") notEvaluable += 1;
        if (view.tone === "unscored") notScoredYet += 1;
        if (view.tone === "legacy") otherRubric += 1;
        // In flight is not "no score" yet — it is not there to explain.
        if (view.tone !== "queued" && view.tone !== "running" && view.tone !== "none") {
          unscored.push({ testCaseId: id, column, cell, label: view.text, reason: view.title ?? null });
        }
      }
      if (column.isBaseline) continue;
      const from = baseScores.get(id) ?? null;
      if (from == null || score == null) continue;
      const delta = score - from;
      if (delta > DELTA_EPSILON) improved += 1;
      else if (delta < -DELTA_EPSILON) worsened += 1;
      else same += 1;
      movers.push({ testCaseId: id, column, from, to: score, delta });
    }
    return {
      column,
      stats,
      delta: !column.isBaseline && stats.avg != null && baseAvg != null ? stats.avg - baseAvg : null,
      improved: column.isBaseline ? null : improved,
      worsened: column.isBaseline ? null : worsened,
      same: column.isBaseline ? null : same,
    };
  });

  return {
    columns: summaries,
    avg: scored ? sum / scored : null,
    avgAnswerQuality: meanOf(answerQuality),
    avgExecution: meanOf(execution),
    combos: columns.length * ids.length,
    scored,
    failed,
    notEvaluable,
    notScoredYet,
    otherRubric,
    commonRubric,
    improved: movers
      .filter((m) => m.delta > DELTA_EPSILON)
      .sort((a, b) => b.delta - a.delta)
      .slice(0, 5),
    worsened: movers
      .filter((m) => m.delta < -DELTA_EPSILON)
      .sort((a, b) => a.delta - b.delta)
      .slice(0, 5),
    unscored,
  };
}
